/** Business HQ spend (`/api/admin/v2/business`). Amounts keep their own currency. */

export type Money = { amount: number; currency: string };
export type VendorCategory = 'saas' | 'hardware' | 'services' | 'facilities' | 'staffing' | 'marketing' | 'other';
export type Cadence = 'monthly' | 'quarterly' | 'yearly';
export type BillStatus = 'draft' | 'approved' | 'paid' | 'void';
export type ExpenseStatus = 'submitted' | 'approved' | 'rejected' | 'reimbursed' | 'cancelled';

export interface BizVendor {
  id: string;
  name: string;
  category: VendorCategory;
  integrationName?: string | null;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  defaultCurrency?: string | null;
  paymentTermsDays?: number | null;
  taxId: string;
  notes: string;
  status: 'active' | 'archived';
  owed?: Money[];
  activeContracts?: number;
  recurringMonthly?: Money[];
  /** This month's metered spend when linked to an integration. */
  integrationSpend?: { amount: number | null; currency: string | null; calls?: number | null } | null;
}

export interface IntegrationCandidate {
  integrationName: string;
  vendorName: string;
  category: string;
  plan: string;
  nameTaken: boolean;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  code: string;
  status: 'active' | 'archived';
}

export interface BizContract {
  id: string;
  vendorId: string;
  title: string;
  startDate?: string | null;
  endDate?: string | null;
  autoRenew: boolean;
  noticeDays?: number | null;
  value?: number | null;
  currency?: string | null;
  cadence: 'one_off' | Cadence;
  status: 'active' | 'ended';
  ownerId?: string | null;
  hasDocument: boolean;
  notes: string;
  cancelBy?: string | null;
  renewingSoon: boolean;
  daysToCancelBy?: number | null;
}

export interface RecurringCharge {
  id: string;
  vendorId: string;
  description: string;
  categoryId?: string | null;
  departmentId?: string | null;
  contractId?: string | null;
  amount: number;
  currency: string;
  cadence: Cadence;
  nextChargeOn: string;
  lastBilledOn?: string | null;
  status: 'active' | 'paused' | 'ended';
}

export interface Bill {
  id: string;
  vendorId: string;
  number: string;
  issueDate: string;
  dueDate?: string | null;
  amount: number;
  currency: string;
  categoryId?: string | null;
  departmentId?: string | null;
  description: string;
  recurringChargeId?: string | null;
  status: BillStatus;
  overdue: boolean;
  approvedAt?: string | null;
  paidOn?: string | null;
  paymentRef: string;
  hasAttachment: boolean;
  notes: string;
}

export interface Expense {
  id: string;
  employeeId: string;
  spentOn: string;
  categoryId?: string | null;
  description: string;
  amount: number;
  currency: string;
  hasReceipt: boolean;
  receiptName: string;
  status: ExpenseStatus;
  decidedBy?: string | null;
  decidedAt?: string | null;
  decisionNote: string;
  reimbursedAt?: string | null;
  reimbursedRef: string;
  createdAt?: string | null;
}

export interface Budget {
  id: string;
  /** YYYY-MM */
  month: string;
  departmentId?: string | null;
  categoryId?: string | null;
  amount: number;
  currency: string;
  actual: number;
}

export interface BudgetView {
  budgets: Budget[];
  spend: { month: string; departmentId?: string | null; categoryId?: string | null; currency: string; amount: number }[];
}

export interface WorkplaceExpenses {
  expenses: Expense[];
  categories: ExpenseCategory[];
  receiptsEnabled: boolean;
}

export const VENDOR_CATEGORY_LABEL: Record<VendorCategory, string> = {
  saas: 'Software & SaaS',
  hardware: 'Hardware',
  services: 'Services',
  facilities: 'Facilities',
  staffing: 'Staffing',
  marketing: 'Marketing',
  other: 'Other',
};

export const BILL_STATUS_BADGE: Record<BillStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-sky-50 text-sky-700',
  paid: 'bg-emerald-50 text-emerald-700',
  void: 'bg-gray-100 text-gray-400 line-through',
};

export const EXPENSE_STATUS_BADGE: Record<ExpenseStatus, string> = {
  submitted: 'bg-amber-50 text-amber-800',
  approved: 'bg-sky-50 text-sky-700',
  rejected: 'bg-red-50 text-red-700',
  reimbursed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const EXPENSE_STATUS_LABEL: Record<ExpenseStatus, string> = {
  submitted: 'Waiting for approval',
  approved: 'Approved, to reimburse',
  rejected: 'Rejected',
  reimbursed: 'Reimbursed',
  cancelled: 'Withdrawn',
};

// ------------------------------------------------------------------ revenue

export type RevenueLine = 'promotions' | 'subscriptions' | 'store_plans' | 'addons' | 'other' | 'commission' | 'other_income';

export interface RevenueRow {
  month: string;
  line: RevenueLine;
  currency: string;
  gross: number;
  refunds: number;
  net: number;
}

export interface OtherIncome {
  id: string;
  receivedOn: string;
  kind: 'services' | 'partnership' | 'sponsorship' | 'grant' | 'interest' | 'refund_received' | 'other';
  source: string;
  description: string;
  amount: number;
  currency: string;
  reference: string;
}

/** `GET /api/admin/v2/business/revenue?from&to` — never converted between currencies. */
export interface RevenueView {
  from: string;
  to: string;
  months: string[];
  rows: RevenueRow[];
  totals: { currency: string; gross: number; refunds: number; net: number }[];
  noncash: { month: string; currency: string; amount: number }[];
  topCustomers: { kind: 'user' | 'org'; id: number; name: string; currency: string; amount: number }[];
  otherIncome: OtherIncome[];
}

/** Fixed order = fixed colour slot (never re-assigned by rank). */
export const REVENUE_LINES: { id: RevenueLine; label: string; light: string; dark: string }[] = [
  { id: 'promotions', label: 'Promotions & ads', light: '#2a78d6', dark: '#3987e5' },
  { id: 'subscriptions', label: 'Subscriptions', light: '#eb6834', dark: '#d95926' },
  { id: 'store_plans', label: 'Store plans', light: '#1baf7a', dark: '#199e70' },
  { id: 'addons', label: 'Add-ons', light: '#eda100', dark: '#c98500' },
  { id: 'other', label: 'Other sales', light: '#e87ba4', dark: '#d55181' },
  { id: 'commission', label: 'Commission', light: '#008300', dark: '#008300' },
  { id: 'other_income', label: 'Other income', light: '#4a3aa7', dark: '#9085e9' },
];

export const INCOME_KIND_LABEL: Record<OtherIncome['kind'], string> = {
  services: 'Services / consulting',
  partnership: 'Partnership',
  sponsorship: 'Sponsorship',
  grant: 'Grant',
  interest: 'Bank interest',
  refund_received: 'Refund received',
  other: 'Other',
};

// ------------------------------------------------------------------ reports

/** One month x currency of a two-sided report (P&L: income/costs; cash: inflow/outflow). */
export type ReportRow<A extends string, B extends string> = {
  month: string;
  currency: string;
  totals: Record<A | B, number>;
  net: number;
} & Record<A | B, Record<string, number>>;

export interface Report<A extends string, B extends string> {
  from: string;
  to: string;
  months: string[];
  rows: ReportRow<A, B>[];
  totals: ({ currency: string; net: number } & Record<A | B, number>)[];
}

export type PnlReport = Report<'income', 'costs'>;
export type CashReport = Report<'inflow', 'outflow'>;

export interface AccountingOverview {
  month: string;
  pnl: PnlReport;
  cash: CashReport;
  owed: Money[];
  overdue: Money[];
  expensesOpen: Money[];
  payRunsOpen: { id: string; period: string; status: 'draft' | 'approved' }[];
}

// ------------------------------------------- phase 9: compliance, assets, POs

export type ComplianceKind = 'registration' | 'license' | 'permit' | 'certificate' | 'insurance' | 'tax_clearance' | 'other';

export interface ComplianceItem {
  id: string;
  kind: ComplianceKind;
  name: string;
  issuer: string;
  number: string;
  holder: string;
  issuedOn?: string | null;
  expiresOn?: string | null;
  renewLeadDays: number;
  status: 'valid' | 'expiring_soon' | 'expired' | 'revoked';
  daysToExpiry?: number | null;
  ownerId?: string | null;
  hasDocument: boolean;
  notes: string;
  /** Write-only: mark revoked. */
  revoked?: boolean;
}

export const COMPLIANCE_KIND_LABEL: Record<ComplianceKind, string> = {
  registration: 'Business registration',
  license: 'Licence',
  permit: 'Permit',
  certificate: 'Certificate',
  insurance: 'Insurance',
  tax_clearance: 'Tax clearance',
  other: 'Other',
};

export const COMPLIANCE_STATUS_BADGE: Record<ComplianceItem['status'], { label: string; className: string }> = {
  valid: { label: 'Valid', className: 'bg-emerald-50 text-emerald-700' },
  expiring_soon: { label: 'Renew soon', className: 'bg-amber-50 text-amber-800' },
  expired: { label: 'Expired', className: 'bg-red-50 text-red-700' },
  revoked: { label: 'Revoked', className: 'bg-gray-100 text-gray-500' },
};

export type AssetCategory = 'laptop' | 'phone' | 'tablet' | 'monitor' | 'accessory' | 'furniture' | 'vehicle' | 'other';

export interface BizAsset {
  id: string;
  tag: string;
  name: string;
  category: AssetCategory;
  serial: string;
  status: 'in_stock' | 'assigned' | 'repair' | 'retired' | 'lost';
  assigneeId?: string | null;
  location: string;
  vendorId?: string | null;
  purchasedOn?: string | null;
  cost?: number | null;
  currency?: string | null;
  warrantyUntil?: string | null;
  notes: string;
}

export const ASSET_STATUS_BADGE: Record<BizAsset['status'], { label: string; className: string }> = {
  in_stock: { label: 'In stock', className: 'bg-gray-100 text-gray-700' },
  assigned: { label: 'Assigned', className: 'bg-sky-50 text-sky-700' },
  repair: { label: 'In repair', className: 'bg-amber-50 text-amber-800' },
  retired: { label: 'Retired', className: 'bg-gray-100 text-gray-400' },
  lost: { label: 'Lost', className: 'bg-red-50 text-red-700' },
};

export interface PoLine {
  description: string;
  quantity: number;
  unitPrice: number;
  amount?: number;
}

export interface PurchaseOrderV2 {
  id: string;
  number: string;
  vendorId: string;
  requestedBy?: string | null;
  departmentId?: string | null;
  categoryId?: string | null;
  lines: PoLine[];
  currency: string;
  total: number;
  status: 'draft' | 'submitted' | 'approved' | 'ordered' | 'received' | 'cancelled';
  neededBy?: string | null;
  approvedAt?: string | null;
  billId?: string | null;
  notes: string;
  createdAt?: string | null;
}

export const PO_STATUS_BADGE: Record<PurchaseOrderV2['status'], string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-amber-50 text-amber-800',
  approved: 'bg-sky-50 text-sky-700',
  ordered: 'bg-violet-50 text-violet-700',
  received: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-400',
};
